import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';
import * as BasketNS from '@emersya/mpa-basket';
// import type * as TConfigurationEditor from '@emersya/mpa-application/src/configuration-editor/index';
import {
	Loader as LoaderNS,
	// Text as TextNS,
	Input as InputNS,
	InputSelect as InputSelectNS,
	InputImage as InputImageNS,
	InputButton as InputButtonNS,
	Image as ImageNS,
	ModalsZone as ModalsZoneNS,
	Application as ApplicationNS,
	ShortcutsManager as ShortcutsManagerNS
} from '@emersya/mpa-components';

import './Application.scss';
/** Necessary to reference this file for the engine part to be reused in configuration-converter **/
import type * as IndexNS from '../../index';

<typeof IndexNS><any>void 1;

declare const ENGINE_VERSION: string; /* Filed by the build. */

/** TODO : Move to planner component **/
declare type TViewpoint =
	'top'
	| 'front'
	| 'frontLeft'
	| 'frontRight'
	| 'left'
	| 'right'
	| 'back'
	| 'backLeft'
	| 'backRight';

export namespace PMS {
	export interface Entity {
		readonly SKU: string;
		readonly translations: Record<string, STD.translationsManager.TranslationMapping> & {
			'@name': STD.translationsManager.TranslationMapping;
			'@description': STD.translationsManager.TranslationMapping;
		};
		readonly metadata: Record<string, unknown> | null;
	};

	export interface TDimension {
		min: number;
		default: number;
		max: number;
	};

	export interface D3Model<TD3 extends {}> extends Entity {
		readonly iconURL: string;
		readonly categoryPath: string;
		readonly pricing: BasketNS.Pricing.TPricingSerialized | null;
		readonly d3: {
			readonly projectCode: string;
			readonly viewer: {
				readonly routingCode: string;
			};
			readonly boundingBox: {
				readonly width: number;
				readonly height: number;
				readonly depth: number;
				readonly center: STD.object.FixedArray<Float32Array, 3>;
				readonly radius: number;
			};
		} & TD3;
		readonly configurations: Configuration[];
	};

	export interface Product extends D3Model<{
		readonly configurationCode: string | null
	}> {
		readonly 'is-dimension-editable': boolean;
		readonly dimensions: {
			width: TDimension;
			depth: TDimension;
			height: TDimension;
		};
	};

	export interface Object extends D3Model<{}> {
		readonly type: string;
		readonly context: {
			/** Object will be available only if the product is $. **/
			readonly forProducts: string/** Product.SKU. **/[];
		};
	};

	export interface Configuration extends Entity {
		readonly type: 'preset' | 'group';
		readonly pricing: BasketNS.Pricing.TPricingSerialized | null;
		readonly context: {
			/**
			 * Configuration will be available only if the product is $ and the object is $$.
			 * If the object is not allowed for product $, throw an error.
			 **/
			readonly forProducts: string/** Product.SKU. **/[];
			// readonly forObjects: string/** Object.SKU. **/[];
			readonly options: Option[];
			readonly defaultOption: Record<
				string, /* Product.SKU */
				string /* Option.SKU */
			>
		};
	};

	export interface Option extends Entity {
		readonly name: string;
		readonly thumbnail: {
			readonly type: 'image' | 'color';
			readonly value: string;
		};
		readonly pricingInfo: {
			readonly pricing: BasketNS.Pricing.TPricingSerialized;
			readonly volumeComputationMethod: BasketNS.IOptionVolumeComputation;
		} | null;
		readonly context: {
			/**
			 * Option will be available only if the product is $, the object is $$ and the configuration is $$$.
			 * If the object is not allowed for product $, throw an error.
			 * If the configuration is not allowed for the object $$, throw an error.
			 **/
			readonly forProducts: string/** Product.SKU. **/[];
			// readonly forObjects: string/** Object.SKU. **/[];
			// readonly forConfigurations: string/** Configuration.SKU. **/[];
		};
	};
}


export interface IEngineApplicationComponentConfiguration extends ApplicationNS.Configuration {
	// readonly devAssetsURL : string | null;
	// readonly controllerName : string;
	readonly productionAppCode: string;
	readonly productionCustomerCode: string;
	readonly languages: {
		readonly list: {
			readonly  id: string;
			readonly  name: string;
		}[];
		readonly default: string;
	};
	readonly countries: {
		readonly list: {
			readonly  id: string;
			readonly  currencyId: string;
			readonly  VAT: number;
		}[];
		readonly default: string;
	};
	readonly currencies: {
		readonly list: {
			readonly  id: string;
			readonly  symbol: string;
		}[];
	};
	/** Used to sort user inputs before replay, small numbers are applied before. **/
	readonly replayUserInputsSorter?: (
		path: string,
		boundComponent: CM.Component<any, any>,
		currentOrder: number
	) => number;
	readonly modules: EngineModule.IModuleInfos[];
	readonly pdfTranslations: Record<string, Record<string, string>>;


	readonly PMS: {
		readonly products: PMS.Product[];
		readonly objects: PMS.Object[];
		readonly productsAndObjectsCategories: Record</** Category hierarchy path. **/string, {
			readonly translations: Record<string, STD.translationsManager.TranslationMapping>;
		}>;
	};

	readonly theme: {
		foreground: Record<'colorful-light' | 'colorful-medium' | 'colorful-dark' | 'colorless-light' | 'colorless-medium' | 'colorless-dark' | 'success-light' | 'success-medium' | 'success-dark' | 'warning-light' | 'warning-medium' | 'warning-dark' | 'error-light' | 'error-medium' | 'error-dark', string>;
		background: Record<'colorful-light' | 'colorful-medium' | 'colorful-dark' | 'colorless-light' | 'colorless-medium' | 'colorless-dark' | 'success-light' | 'success-medium' | 'success-dark' | 'warning-light' | 'warning-medium' | 'warning-dark' | 'error-light' | 'error-medium' | 'error-dark', string>;
		text: Record<string, Record<'font-size' | 'font-family' | 'font-weight' | 'letter-spacing' | 'overflow-wrap' | 'tab-size' | 'text-transform' | 'white-space' | 'text-indent' | 'word-spacing' | 'text-decoration' | 'text-shadow', string>>;
		customCSS: Record<string, string>;
		fonts: Record<string, string>;
	};

	readonly users: Record<
		/* user id */ string,
		/* role id */ string
	>

	readonly useStepConfiguratorMenu: boolean;

	readonly OMSMode: 'quote' | 'order' | null;
};
export type TMessagesListenerComponent = CM.Component.TAny & {
	readonly  onMessage: (event: MessageEvent) => Promise<void> | void;
};

export class EngineApplicationComponent<
	TComponent extends EngineApplicationComponent<TComponent, TConfiguration>,
	TConfiguration extends IEngineApplicationComponentConfiguration
> extends ApplicationNS.Component<
	TComponent,
	TConfiguration
> {
	static clientAppParameters: NS.IClientAppParameters;

	/*
		Implement this static method in your application specific class to update if necessary the configuration before it will be used.
		If the configuration do not need update, simply implement it without doing anything.
		You must edit "this.managerManifest.configurations" for your's modifications to be taken into account.
		/!\ Is an abstract static method but TS doesnt support this so do black magick!
	*/
	static configurationNormalize(): void {
	};

	/** load the dependencies + instantiate the app **/
	static async run() {
		if (this.count !== 0) {
			throw STD.errors.user('Only one application component is allowed!', {
				EngineApplicationComponent
			});
		}
		try {
			await STD.DOM.awaitDocumentIsInteractive();
			EngineApplicationComponent.clientAppParameters = (<any>window).serverParameters;
			if (typeof this.clientAppParameters !== 'object') {
				throw new Error;
			}
			STD.DOM.openFilesSelector.initialize(document.body as HTMLBodyElement);
			CM.Manager.initialize(() => {
			});
			const confDownloadResponse = await fetch(this.clientAppParameters.app.configuration.currentPath);
			const configuration = (
				(await confDownloadResponse.text()).replace(
					/\$ASSETS\//gm,
					this.clientAppParameters.app.assetsPath
				)
			);
			const manifest = CM.Manifest.create(
				this.clientAppParameters.app.configuration.currentPath,
				configuration,
				'manager'
			);
			this.managerManifest = manifest;
			this.configurationNormalize();
			const confK = <IEngineApplicationComponentConfiguration | null>this.managerManifest.treeNodes.find(x => x.id ===
				'K')?.configuration || null;
			if (!confK) {
				throw new Error;
			}
			STD.translationsManager.initialize({
				languages: confK.languages.list.map(x => x.id),
				defaultLanguage: confK.languages.default,
				translations: {} // Dynamically filled.
			});
			STD.translationsManager.use(this.clientAppParameters.user.lang);
			const rootComponent = <CM.DOMComponent<any, any, any>>await manifest.instantiate();
			if (!(rootComponent instanceof EngineApplicationComponent)) {
				throw STD.errors.user(
					'Root component must be an EngineApplicationComponent!',
					{rootComponent}
				);
			}
			document.body.appendChild(rootComponent.component_DOM);
			await rootComponent.component_start(rootComponent.component_getEventWhichRepresentNoEvent());
			await rootComponent.component_dispatchVisibilityUpdate(true);
			return rootComponent;
		} catch (error: any) {
			throw STD.errors.bindAsBound('Cannot initialize the application', {}, error);
		}
	};

	static override initialize() {
		super.initialize.call(this);
		/** TODO : no standing code here! **/
		this.declareEventType('standDimensions-update');
		registerModuleBasket();
		registerModulePlanner();
		registerModulePlannerLayoutEditor();
		registerModulePlannerDimensionsEditor();
		registerModulePlannerFloorEditor();
		registerModulePlannerWallsEditor();
		registerModulePlannerFurniture();
		registerModuleProductChoose();
		registerModuleCustomerInformationForm();
		registerModuleCustomerInformationFormModal();
		registerModuleCommandConfirmationModal();
		registerModuleLanguageSelector();
		registerModulePlannerMenu();
		registerModuleHeaderMenu();
		registerModuleHeaderFullWidth();
		registerModuleHeaderViewer();
		registerModulePlannerViewerFloorWalls();
		registerModuleHome();
		registerModuleReplayLinkModal();
	};

	static get version() {
		return ENGINE_VERSION;
	};

	viewer!: ViewerNS.Component;
	historyManager!: HistoryManagerNS.Component;
	shortcutsManager!: ShortcutsManagerNS.Component;
	isReloading = false;
	isApplyingPreviousUserInputs = false;
	isDimensionEditorDisplayed = false;
	isProductEditorDisplayed = false;
	previousConfigurationMetadata: Awaited<ReturnType<typeof this.generateConfigurationMetadata>> | null = null;
	private _currentCountry: string;
	private unusableCount = 0;


	get currentLanguage() {
		return STD.translationsManager.language;
	};

	/**
	 * Get the current country object from private current country id.
	 **/
	get currentCountry() {
		const country = this.component_configuration.countries.list.find(x => x.id === this._currentCountry);
		if (!country) {
			this.component_throwLibraryError('Current country id does not match any country!', {
				currentCountryId: this._currentCountry,
				countriesList: this.component_configuration.countries.list
			});
		}
		return country;
	};

	/**
	 * Get the current currency bound to the current country.
	 **/
	get currentCurrency() {
		const country = this.currentCountry;
		const currency = this.component_configuration.currencies.list.find(x => x.id === country.currencyId);
		if (!currency) {
			this.component_throwLibraryError('Currency id defined by the country currency does not match any currency!', {
				currentCountry: country,
				currenciesList: this.component_configuration.currencies.list
			});
		}
		return currency;
	};

	get code() {
		if (!this.clientAppParameters.server.isOnProd) {
			return app.parameters.useLocalViewers ?
				this.clientAppParameters.app.code :
				this.component_configuration.productionAppCode;
		}
		return this.clientAppParameters.app.code;
	};

	get configurationFileVersionId() {
		return this.clientAppParameters.app.configuration.versionId;
	};

	get version() {
		return ENGINE_VERSION;
	};

	getLogoFileName(): string {
		return 'logo.png';
	}

	async controllerGET(descriptor: {
		readonly route: string;
		readonly parameters: Record<string, string>;
		readonly headers: Record<string, string>;
	}): Promise<Response> {
		const URL = STD.net.URL_format(
			`${this.clientAppParameters.app.URLPrefix}${descriptor.route}`,
			descriptor.parameters
		);
		return await fetch(URL, {
			method: 'GET',
			mode: 'cors',
			cache: 'no-cache',
			headers: descriptor.headers
		});
	};

	async controllerPOST(descriptor: {
		readonly route: string;
		readonly parameters: Record<string, string>;
		readonly headers: Record<string, string>;
		readonly body: BodyInit | null;
	}): Promise<Response> {
		const URL = STD.net.URL_format(
			(app.clientAppParameters.server.isOnProd || app.parameters.useLocalViewers) ?
				`${this.clientAppParameters.app.URLPrefix}${descriptor.route}` :
				`https://emersya.com/${this.currentLanguage}/${this.clientAppParameters.app.isTestMode ?
					'testApp' :
					'app'}/${this.component_configuration.productionAppCode}/${descriptor.route}`,
			descriptor.parameters
		);
		const response = await fetch(URL, {
			method: 'POST',
			mode: 'cors',
			cache: 'no-cache',
			body: descriptor.body,
			headers: descriptor.headers
		});
		if (!response.ok) {
			const errorText = await response.text();
			let shouldRethrowNewError = true;
			try {
				const emersyaError = JSON.parse(errorText);
				shouldRethrowNewError = false;
				throw STD.errors.library(emersyaError.description, {response, emersyaError});
			} catch (error) {
				if (shouldRethrowNewError) {
					throw STD.errors.library(errorText, {response});
				}
				throw error;
			}
		}
		return response;
	};

	get clientAppParameters() {
		return EngineApplicationComponent.clientAppParameters;
	};

	setAppUsable() {
		this.unusableCount--;
		if (this.unusableCount !== 0) {
			return;
		}
		document.body.style.cursor = '';
		this.component_DOM.classList.remove('unusable');
	};

	setAppUnusable() {
		this.unusableCount++;
		document.body.style.cursor = 'wait';
		/** Do not set the app as unusable when loader displayed because it will collide with the loader style **/
		if ((this.component_findOuter(['loader']) as any).isVisible) {
			return;
		}
		this.component_DOM.classList.add('unusable');
	};

	getAllSerializableUserInputs() {
		const allSerializableInputs = <InputNS.Component<any, any, any, any, any>[]>this.getAllChilds().
		filter(x => x instanceof InputNS.Component);
		const unwantedInputs = allSerializableInputs.filter(x => x.component_configuration.disableMPAEngineAutoSerialization);
		const maybeWantedInput = allSerializableInputs.filter(x => !unwantedInputs.includes(x));
		const unwantedInputsPath = unwantedInputs.map(x => x.component_getPath());
		const wantedInputs = maybeWantedInput.filter(x => {
			const currentPath = x.component_getPath();
			return unwantedInputsPath.every(unwantedPath => !currentPath.startsWith(unwantedPath));
		});
		return wantedInputs;
	};

	async getAndSerializeAllUserInputsData(): Promise<[CM.Path.TPathRaw, unknown][]> {
		const allAppInputs = (
			(await Promise.all(
				this.getAllSerializableUserInputs().
				map<Promise<[string, unknown]>>(async (x) => [x.component_getPath(), await x.getUserValueSerialized()])
			)).filter(x => x[1] !== null)
		);
		return allAppInputs;
	};

	async setAndDeserializeAllUserInputsData(
		userInputs: [CM.Path.TPathRaw, unknown][]
	) {
		const defaultGetNewOrder = (
			path: string,
			boundComponent: CM.Component<any, any>,
			currentOrder: number
		) => {
			void path;
			void boundComponent;
			return currentOrder;
		};
		const getNewOrder = this.component_configuration.replayUserInputsSorter || defaultGetNewOrder;
		let toBeDeserialized = Array.from(userInputs).filter(x => !x[0].startsWith('K.visualization.V.viewer-menu'));
		let hasDoneChanges = true;
		while (toBeDeserialized.length && hasDoneChanges) {
			hasDoneChanges = false;
			const {
				remaining,
				toDeserializeThisStep
			} = toBeDeserialized.reduce<{
				readonly remaining: typeof toBeDeserialized;
				readonly toDeserializeThisStep: [string, InputNS.Component<any, any, any, any, any>, number, unknown][];
			}>((a, [path, value], i) => {
				const component = this.component_findMaybe(path);
				if (!component) {
					a.remaining.push([path, value]);
				} else if (!(component instanceof InputNS.Component)) {
					this.component_throwUserError(
						`Given user input do not match current tree, component<${path}> is not an input!`,
						{
							userInputs,
							path,
							value,
							component
						}
					);
				} else {
					//  this is really necessary??!
					if (component.static.type !== 'viewer-graphics') {
						a.toDeserializeThisStep.push([path, component, getNewOrder(path, component, i + 1000), value]);
						// continue;
					}
				}
				return a;
			}, {
				remaining: [],
				toDeserializeThisStep: []
			});
			toBeDeserialized = remaining;
			toDeserializeThisStep.sort((a, b) => a[2] - b[2]);
			if (toDeserializeThisStep.length) {
				hasDoneChanges = true;
			}
			for (const [unused0, component, unused1, value] of toDeserializeThisStep) {
				void [unused0, unused1];
				await component.updateValue(await component.deserialize(value));
			}
		}
		if (toBeDeserialized.length) {
			this.component_throwLibraryError('Cannot deserialize all data!', {
				undeserializable: toBeDeserialized
			});
		}
	};

	override async component_start(startEvent: STD.events.Event<any, CM.Component<any, any>>) {
		const loader = <LoaderNS.Component | undefined>this.component_outerChilds.find(
			x => x instanceof LoaderNS.Component
		);
		if (loader) {
			this.loader = loader;
			await this.loader.update('app.load', null);
		}
		STD.translationsManager.update(document.body);
		if (this.basket) {
			// Mais à quoi sert ce code ???!
			const debouncer = STD.misc.createDebouncerPromise({
				delay: 50,
				fn: this.onBasketPartReferenceUpdated.bind(this)
			});
			this.basket.addEventListener(
				'basket-update',
				async (...x: any[]) => {
					const prom = debouncer(...x);
					return (await new Promise<unknown>((res, rej) => {
						let promEnded = false;
						setTimeout(() => {
							if (!promEnded) {
								res(void 1);
							}
						}, 1000);
						prom.then((...result: any[]) => {
							promEnded = true;
							res(result);
						}).catch(rej);
					}));
				},
				{
					listener: this,
					subType: 'partReferenceUpdated',
					isAtTarget: true
				}
			);


		}
		// this.viewer.addEventListener(
		// 	'message',
		// 	this.onMeshHighlight.bind(this),
		// 	{
		// 		listener :   this,
		// 		isAtTarget : true,
		// 		subType :    'onMeshHighlight'
		// 	}
		// );
		if (this.isReloading) {
			const CAP = this.clientAppParameters;
			if (!CAP.server.isOnProd) {
				(<any>this.clientAppParameters.server).webserviceURL =
					`https://${CAP.app.isTestMode ? 'testws' : 'ws'}.emersya.com/ws`;
			}
			await this.reload();
			if (!this.previousConfigurationMetadata) {
				this.component_throwLibraryError('Reloading without configuration metadata!', {});
			}
			await super.component_start(startEvent);
			this.isApplyingPreviousUserInputs = true;
			// await this.component_find('@visualization.V').component_start(ev);
			const userInputs: [CM.Path.TPathRaw, unknown][] = this.previousConfigurationMetadata.userInputs.filter(x => !x[0].includes(
				'K.modules:panel.standing-counter.content:colors'));
			const defaultGetNewOrder = (
				path: string,
				boundComponent: CM.Component<any, any>,
				currentOrder: number
			) => {
				if (path === 'K.visualization.V') {
					return 0;
				}
				void path;
				void boundComponent;
				return currentOrder + 100;
			};
			const getNewOrder = this.component_configuration.replayUserInputsSorter || defaultGetNewOrder;
			let toBeDeserialized = Array.from(userInputs).filter(x => (
				!x[0].startsWith('K.visualization.V.viewer-menu') &&
				!x[0].startsWith('K.customConfigurators')
			));
			let hasDoneChanges = true;
			while (toBeDeserialized.length && hasDoneChanges) {
				hasDoneChanges = false;
				const {
					remaining,
					toDeserializeThisStep
				} = toBeDeserialized.reduce<{
					readonly remaining: typeof toBeDeserialized;
					readonly toDeserializeThisStep: [string, InputNS.Component<any, any, any, any, any>, number, unknown][];
				}>((a, [path, value], i) => {
					const component = this.component_findMaybe(path);
					if (!component) {
						a.remaining.push([path, value]);
					} else if (!(component instanceof InputNS.Component)) {
						this.component_throwUserError(
							`Given user input do not match current tree, component<${path}> is not an input!`,
							{
								userInputs,
								path,
								value,
								component
							}
						);
					} else {
						a.toDeserializeThisStep.push([path, component, getNewOrder(path, component, i + 1000), value]);
					}
					return a;
				}, {
					remaining: [],
					toDeserializeThisStep: []
				});
				toBeDeserialized = remaining;
				toDeserializeThisStep.sort((a, b) => a[2] - b[2]);
				if (toDeserializeThisStep.length) {
					hasDoneChanges = true;
				}
				for (const [unused0, component, unused1, value] of toDeserializeThisStep) {
					if ((<any>value)?.disableMPAEngineAutoDeserialization || component.static.type === 'viewer-graphics') {
						continue;
					}
					void [unused0, unused1];
					await component.updateValue(await component.deserialize(value));
				}
			}
			if (toBeDeserialized.length) {
				this.component_throwLibraryError('Cannot deserialize all data!', {
					nonDeserializable: toBeDeserialized
				});
			}
			this.isApplyingPreviousUserInputs = false;
			if (this.basket) {
				await this.basket.basket.dispatchEvent('basket-update', {
					subType: 'productUsed',
					details: {
						product: this.currentProduct
					}
				});
			}
			// await super.component_start(ev);
			// this.isApplyingPreviousUserInputs = true;
			// // await this.component_find('@visualization.V').component_start(ev);
			// await this.setAndDeserializeAllUserInputsData(this.previousConfigurationMetadata.userInputs.filter(x => !x[ 0 ].includes(
			// 	'K.modules:panel.standing-counter.content:colors')));
			// this.isApplyingPreviousUserInputs = false;
			this.isReloading = false;
		} else {
			await this.component_find('@visualization.V').component_start(startEvent);
			await super.component_start(startEvent);
		}
		await this.evaluateModulesConditions();
		// const customerMode = app.modules.used.find(x => x.id === 'standing-customerMode');
		// if (customerMode) {
		// 	await (<StandingCustomerModeNS.Component> customerMode.component).onAppReady();
		// }
		STD.translationsManager.update(document.body);
		await this.component_find('@.loader').component_disable();
	};

	getAppURL() {
		const CAP = this.clientAppParameters;
		const appMode = CAP.app.isTestMode ? 'testApp' : 'app';
		return `${CAP.server.protocol}${CAP.server.domain}/${this.currentLanguage}/${appMode}/${this.code}`;
	};

	async loadSavedConfigurationMetadata() {
		const s = this.clientAppParameters;
		if (!s.app.savedConfigurationCode) {
			return null;
		}
		const requestURL = STD.net.URL_format(s.server.webserviceURL + '/configuration/metadata/getPath', {
			configurationCode: s.app.savedConfigurationCode
		});
		const response = (await (await fetch(requestURL)).json()).resultSet;
		if (response.error) {
			this.component_throwUserError('Cannot fetch saved configuration metadata!', {
				serverError: response.error,
				configurationCode: s.app.savedConfigurationCode
			});
		}
		const metadata = await fetch(response.data[0]);
		return metadata;
	};

	protected override async component_finalize() {
		await super.component_finalize();
		STD.translationsManager.update(document.body);
	};

	private async onBeforeUnload(nativeEvent: BeforeUnloadEvent) { // eslint-disable-line require-await
		nativeEvent.preventDefault();
		nativeEvent.returnValue =
			'Application is going to close and all unsaved works will be lost.\nWould you like to continue?';
		return ('Application is going to close and all unsaved works will be lost.\nWould you like to continue?');
	};

	updateGlobalProductName() {
		if (!this.currentProduct || !this.currentProduct.reference) {
			this.component_throwLibraryError('This method cannot be called if current product is not defined or does not have a reference!', {
				currentProduct: this.currentProduct
			});
		}
		const ref = this.currentProduct.reference;
		// const info = this.component_configuration.products.find(x => x.SKU === ref.value);
		const info = this.component_configuration.PMS.products.find(x => x.SKU === ref.value);
		if (!info) {
			this.component_throwLibraryError('Current product unrelated to the products lists!', {
				currentProduct: this.currentProduct,
				productsList: this.component_configuration.PMS.products
			});
		}

		const headerComponent = (
			this.component_findMaybe('@header') ||
			this.component_findMaybe('@visualization.header')
		);
		if (!headerComponent) {
			throw STD.errors.library('Cannot find header component!', {});
		}
		(<any>headerComponent.component_find('@:productName')).update(info.translations['@name']?.[app.currentLanguage] || info.SKU);
	};

	readonly globalPricingContext = Object.freeze({
		input: 'exclTax',
		output: 'exclTax',
		generatePricingGetBinding(boundTo: string) {
			const [componentPath, propertyPath] = boundTo.split('->');
			if (!componentPath || !propertyPath) {
				throw new Error;
			}
			const propertyOperation = CM.PropertyOperation.create({
				type: 'call',
				componentPath,
				propertyPath,
				parameters: [
					{
						'$.host': 'details.host'
					}
				]
			});
			return async (host: BasketNS.Product | BasketNS.Part | BasketNS.Option) => {
				const event = app.component_getEventWhichRepresentNoEvent();
				event.details.host = host;
				const key = await propertyOperation.execute(app, event);
				if (typeof key !== 'string') {
					throw STD.errors.library('', {
						key,
						propertyOperation
					});
				}
				return key;
			};
		}
	});

	private readonly _basket: OMSBasketNS.Component | null = null;

	get basket(): OMSBasketNS.Component | null {
		return this._basket;
	};

	get modules() {
		return {
			all: EngineModule.all,
			used: EngineModule.used,
			usedWithPageOrdered: EngineModule.usedWithPageOrdered,
			disableds: EngineModule.disableds
		};
	};

	get currentProduct(): BasketNS.Product | null {
		if (
			app.isReloading &&
			!this.basket?.basket.currentProduct &&
			app.previousConfigurationMetadata &&
			app.previousConfigurationMetadata.basket.products.length &&
			this.basket?.basket.products.length
		) {
			const savedProduct = app.previousConfigurationMetadata.basket.products[0];
			if (savedProduct) {
				return this.basket?.basket.products.find(x => x.id === savedProduct.name) || null;
			}
		}
		return this.basket?.basket.currentProduct || null;
	};

	get currentProductSKU() {
		const product = this.currentProduct;
		if (!product || !product.reference) {
			this.component_throwUserError('No current product or it does not have a reference!', {
				currentProduct: this.currentProduct
			});
		}
		return product.reference.value;
	};

	/**
	 * Getter to override by specific an app class to allow having a product used in place of the current product for exemple.
	 **/
	get virtualCurrentProductSKU() {
		return this.currentProductSKU;
	};

	/**
	 * Method to override by specific an app class to allow having a product used in place of the current product for exemple.
	 **/
	areProductsSKURelatedToCurrentProduct(productsSKU: string[]) {
		const p = this.currentProduct;
		if (!p || !p.reference) {
			this.component_throwUserError('No current product or it does not have a reference!', {
				currentProduct: this.currentProduct
			});
		}
		return productsSKU.includes(this.virtualCurrentProductSKU);
	};

	get currentDefaults() {
		const p = this.currentProduct;
		if (!p || !p.reference) {
			this.component_throwUserError('No current product or it does not have a reference!', {
				currentProduct: this.currentProduct
			});
		}
		const PMScurrentProduct = this.component_configuration.PMS.products.find(x => x.SKU === p.reference!.value);
		if (!PMScurrentProduct) {
			this.component_throwUserError('No product with this reference in PMS!', {
				currentProduct: this.currentProduct
			});
		}
		const productConfigurations = PMScurrentProduct.configurations;
		if (!productConfigurations) {
			return {};
		} else {
			return Object.fromEntries(productConfigurations.map(x => {
				return [x.SKU, x.context.defaultOption[p.reference!.value]];
			}));
		}
	};

	get currentOptions() {
		const p = this.currentProduct;
		if (!p || !p.reference) {
			this.component_throwUserError('No current product or it does not have a reference!', {
				currentProduct: this.currentProduct
			});
		}
		const PMScurrentProduct = this.component_configuration.PMS.products.find(x => x.SKU === p.reference!.value);
		if (!PMScurrentProduct) {
			this.component_throwUserError('No product with this reference in PMS!', {
				currentProduct: this.currentProduct
			});
		}
		const productConfigurations = PMScurrentProduct.configurations;
		if (!productConfigurations) {
			return {};
		} else {
			return Object.fromEntries(productConfigurations.map(x => {
				return [x.SKU, x.context.options];
			}));
		}
		// return app.component_configuration.productsOptions.filter(x => (
		// 	this.areProductsSKURelatedToCurrentProduct(x.products)
		// ));
	};

	hasThisModuleUsed(moduleId: string) {
		return !!this.modules.used.find(x => x.id === moduleId);
	};

	async ifHasThisModuleUsed(moduleSuffixe: string, callback: (module: EngineModule.TModule) => Promise<void>): Promise<boolean> {
		const module = this.modules.used.find(x => x.id.endsWith(moduleSuffixe));
		if (module) {
			try {
				await callback(module);
				return true;
			} catch (error) {
				throw error;
			}
		} else {
			return false;
		}
	};


	async useBasket() {
		if (this._basket) {
			throw STD.errors.user('Cannot replace application basket once created!', {
				basket: this._basket
			});
		}
		const basket = <OMSBasketNS.Component>await this.component_instantiateOuterChild<OMSBasketNS.Configuration>({
			type: 'oms-basket',
			id: 'basket',
			tree: this.component_tree,
			slot: null,
			isSlot: false,
			configuration: {
				application: this,
				products: [],
				metaData: {}
			}
		});
		await basket.component_initialize();
		(<any>this)._basket = basket;
	};

	async reload() {
		this.isReloading = true;
		if (this.clientAppParameters.app.savedBasket) {
			this.previousConfigurationMetadata =
				STD.object.clone((<any>this.clientAppParameters.app.savedBasket).metaData.replay);
		} else if (this.clientAppParameters.app.savedConfigurationCode) {
			const metadataResponse = await this.loadSavedConfigurationMetadata();
			if (!metadataResponse) {
				throw new Error('!');
			}
			const metadata = <NonNullable<EngineApplicationComponent<any, any>['previousConfigurationMetadata']>>await metadataResponse.json();
			this.previousConfigurationMetadata = metadata;
		}
		if (!this.previousConfigurationMetadata) {
			throw new Error;
		}
		const metadata = this.previousConfigurationMetadata;
		const getImage = (imageReference: any) => {
			if (typeof imageReference !== 'string') {
				return (imageReference);
			}
			const res = (/<\[\[image=\x00\x00([\s\S]+)\x00\x00\]\]>/g).exec(imageReference);
			if (res === null) {
				return (imageReference);
			}
			if ((res.length < 2) || (res[1] == null)) {
				throw new Error;
			}
			const image = metadata.images[Number.parseInt(res[1])];
			return (image);
		};
		const getImageAsImage = async (imageReference: any) => {
			if (typeof imageReference !== 'string') {
				return (imageReference);
			}
			const res = (/<\[\[image=\x00\x00([\s\S]+)\x00\x00\]\]>/g).exec(imageReference);
			if (res === null) {
				return (imageReference);
			}
			if ((res.length < 2) || (res[1] == null)) {
				throw new Error;
			}
			const imageSrc = <string>metadata.images[Number.parseInt(res[1])];
			const image = new globalThis.Image;
			await new Promise<void>((resolve, reject) => {
				image.onload = () => resolve();
				image.onerror = reject;
				image.src = imageSrc;
			});
			return (image);
		};
		const viewerUserInput = <[string, ViewerNS.ValueSerialized] | undefined>this.previousConfigurationMetadata.userInputs.find(
			x => x[0] ===
				'K.visualization.V');
		if (!viewerUserInput) {
			this.component_throwLibraryError('No viewer saved data found!', {
				metadata: this.previousConfigurationMetadata
			});
		}
		(<STD.object.SetWritable<ViewerNS.ValueSerialized, 'configurationCode'>>viewerUserInput[1]).configurationCode =
			(<any>app).previousConfigurationMetadata.basket.products[0].configurationCode;
		STD.object.visit(this.previousConfigurationMetadata.basket, (container: any, path) => {
			const customImageIndex = Number.parseInt(path.split('').reverse().join());
			if (
				Number.isNaN(customImageIndex) ||
				!path.endsWith(`currentOption.customImages.${customImageIndex}`)
			) {
				return;
			}
			container.sourceFileURL = getImage(container.sourceFileURL);
			container.productionFileURL = getImage(container.productionFileURL);
			container.displayFileURL = getImage(container.displayFileURL);
		});
		if (this.hasThisModuleUsed('oms-basket')) {
			if (!this.basket) {
				this.component_throwLibraryError('Application uses basket but does not have one!', {});
			}
			await this.basket.reloadFromSerialized(this.previousConfigurationMetadata.basket);
		}
		this.previousConfigurationMetadata.userInputs =
			await Promise.all(this.previousConfigurationMetadata.userInputs.map(async ([componentPath, componentUserInput]) => {
				if (typeof componentUserInput == 'string') {
					componentUserInput = await getImageAsImage(componentUserInput);
				} else if (typeof (<any>componentUserInput)?.image === 'string') {
					(<any>componentUserInput).image = await getImageAsImage((<any>componentUserInput).image);
				}
				return (<[string, unknown]>[componentPath, componentUserInput]);
			}));
		for (const tr of this.previousConfigurationMetadata.translations) {
			STD.translationsManager.addObject(tr[0], tr[1]);
		}
	};

	/**
	 * Override this method to override used components type by modules.
	 **/
	getModulesComponentsOverrides(): Parameters<typeof EngineModule.use>[1] {
		return {};
	};

	override async component_initialize() {
		if (this.clientAppParameters.app.savedConfigurationCode || this.clientAppParameters.app.savedBasket) {
			this.isReloading = true;
		}
		(() => {
			// TODO : remove this dev code.
			for (const x of ['planner-structures-static', 'planner-structures-dynamic', 'orders-management', 'pdf', 'email']) {
				const idx = this.component_configuration.modules.findIndex(y => y.id === x);
				if (idx > -1) {
					this.component_configuration.modules.splice(idx, 1);
				}
			}
		})();
		await EngineModule.use(this.component_configuration.modules, this.getModulesComponentsOverrides());
		await super.component_initialize();
		const ViewerComponentProxy = CM.Manager.getClassProxyOrThrow('viewer');
		const instances = ViewerComponentProxy.getInstances() as any as ViewerNS.Component[];
		if (instances.length !== 1) {
			this.component_throwUserError('MPAEngine should have one and only one viewer', {viewers: instances});
		}
		const viewer = instances[0] as ViewerNS.Component;
		if (!viewer || !(viewer instanceof ViewerNS.Component)) {
			this.component_throwLibraryError('Viewer component not found!', {
				viewerComponentPath: null
			});
		}
		this.viewer = viewer;
		const modals = <ModalsZoneNS.Component>this.component_find('@.modals');
		if (!modals || !(modals instanceof ModalsZoneNS.Component)) {
			this.component_throwLibraryError('ModalsZone component not found!', {});
		}
		this.modals = modals;
		this.historyManager = <HistoryManagerNS.Component>this.component_find('@.history-manager');
		this.shortcutsManager = <ShortcutsManagerNS.Component>this.component_find('@.shortcutsManager');
	};

	getScreenshotViewpoint(
		from: TViewpoint
	) {
		const standBB = this.computeStandBoundingBox();
		const diameter = Math.max(standBB.depth, standBB.width);
		const standCenter = {
			x: standBB.minX + (standBB.width / 2.0),
			y: standBB.minY + (standBB.height / 2.0),
			z: standBB.minZ + (standBB.depth / 2.0)
		};
		const d = diameter / .8;
		let x: number = 0;
		let y: number = standBB.height / 2.0;
		let z: number = 0;
		switch (from) {
			case 'front' : {
				z = d;
				break;
			}
			case 'frontLeft' : {
				const p0 = <[number, number]>[0, d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, 45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'frontRight' : {
				const p0 = <[number, number]>[0, d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, -45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'left' : {
				x = -d;
				break;
			}
			case 'right' : {
				x = d;
			}
				break;
			case 'back' : {
				z = -d;
				break;
			}
			case 'backLeft' : {
				const p0 = <[number, number]>[0, -d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, -45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'backRight' : {
				const p0 = <[number, number]>[0, -d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, 45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'top' : {
				x = standCenter.x;
				y = d * 2;
				z = standCenter.y;
				break;
			}
			default:
				throw new Error;
		}
		return {
			position: <Viewer.TVector3>[x, y, z],
			target: <Viewer.TVector3>[standCenter.x, standCenter.y, standCenter.z],
			up: <Viewer.TVector3>[0, 1, 0]
		};
	};

	protected async generateConfigurationMetadata() {
		// @TODO : The planner should not be hardcoded!!!
		if (!this.basket || !this.viewer.planner) {
			this.component_throwLibraryError('Unknown error!', {});
		}
		const basketSerialized = await this.basket.basket.serialize();
		const images: string[] = [];
		const addImage = (img?: null | string | typeof Image): null | string => {
			if (img == null) {
				return (null);
			}
			if (typeof img !== 'string') {
				throw new Error('Image is not a string!');
			}
			let idx = images.indexOf(img);
			if (idx === -1) {
				idx = images.push(img) - 1;
			}
			return (`<[[image=\x00\x00${idx}\x00\x00]]>`);
		};
		STD.object.visit(basketSerialized, (container: any, path) => {
			const customImageIndex = Number.parseInt(path.split('').reverse().join());
			if (
				Number.isNaN(customImageIndex) ||
				!path.endsWith(`currentOption.customImages.${customImageIndex}`)
			) {
				return;
			}
			container.sourceFileURL = addImage(container.sourceFileURL);
			container.productionFileURL = addImage(container.productionFileURL);
			container.displayFileURL = addImage(container.displayFileURL);
		});
		const UI = (
			(await this.getAndSerializeAllUserInputsData()).map(([componentPath, componentUserInput]) => {
				const component = app.component_find(componentPath);
				if (component instanceof ViewerGraphicsNS.Component) {
					(<any>componentUserInput).image = addImage((<ViewerGraphicsNS.ValueSerialized>componentUserInput).image);
				} else if (component instanceof InputImageNS.Component) {
					componentUserInput = addImage(<InputImageNS.ValueSerialized>componentUserInput);
				}
				return (<[string, unknown]>[componentPath, componentUserInput]);
			})
		);
		const sessionsIds = <string[]>[...(this.previousConfigurationMetadata?.sessionsIds || []), STD.sessionId];
		return {
			images,
			engineVersion: ENGINE_VERSION,
			isFinal: app.parameters.isFinal,
			userInputs: UI,
			basket: basketSerialized,
			sessionsIds: sessionsIds,
			translations: (
				Array.from(STD.translationsManager.store.entries()).filter(x => sessionsIds.some(y => x[0].startsWith(y)))
			)
		};
	};

	async openQuoteAndPDFGenerated() {
		const compo: CM.DOMComponent<any, any, any> | undefined = <any>this.component_find('@quoteAndPDFGenerated');
		if (!compo) {
			this.component_throwLibraryError('Component<quoteAndPDFGenerated> not found!', {});
		}
		await compo.component_enable();
	};

	get isUsingLocalViewer() {
		return this.parameters.useLocalViewers && this.clientAppParameters.app.isTestMode;
	};

	async closeQuoteAndPDFGenerated() {
		const compo: CM.DOMComponent<any, any, any> | undefined = <any>this.component_find('@quoteAndPDFGenerated');
		if (!compo) {
			this.component_throwLibraryError('Component<quoteAndPDFGenerated> not found!', {});
		}
		compo.component_stylize({
			filter: '',
			pointerEvents: ''
		}, 'others');
		await compo.component_disable();
	};

	//@TODO : Too many things hardcoded here, will be incompatible with almost all projects who are not TotalExpo / Standing!!!
	async processOrder() {
		if (!this.basket) {
			this.component_throwUserError('Cannot process order for an app without basket!', {});
		}
		const loader = (<LoaderNS.Component>this.component_find('@loader'));
		await loader.update('basketCreation', null);
		const screenshotsToBeDoneByShape: Record<string, TViewpoint[]> = {
			'shape-none': [
				'frontLeft',
				'front',
				'frontRight',
				'left',
				'right',
				'backLeft',
				'back',
				'backRight',
				'top'
			],
			'shape-no-wall': [
				'frontLeft',
				'front',
				'frontRight',
				'left',
				'right',
				'backLeft',
				'back',
				'backRight',
				'top'
			],
			'shape-back': ['frontLeft', 'front', 'frontRight', 'left', 'right', 'backLeft', 'backRight', 'top'],
			'shape-back-left': ['frontLeft', 'front', 'frontRight', 'right', 'backRight', 'top'],
			'shape-back-right': ['frontLeft', 'front', 'frontRight', 'left', 'backLeft', 'top'],
			'shape-left-back-right': ['frontLeft', 'front', 'frontRight', 'top']
		};
		const currentProduct = this.currentProduct;
		if (!currentProduct) {
			this.component_throwUserError('Process order but there are no current product!', {});
		}
		const layoutPart = currentProduct.getPart(['layout']);
		if (!layoutPart) {
			this.component_throwUserError('', {});
		}
		const layoutOption = layoutPart.currentOption;
		if (!layoutOption || !layoutOption.reference) {
			this.component_throwUserError('', {});
		}
		const screenshotsToBeDone = screenshotsToBeDoneByShape[layoutOption.reference.value];
		if (!screenshotsToBeDone) {
			this.component_throwLibraryError('', {});
		}
		const screenshots = await screenshotsToBeDone.reduce<Promise<string[]>>(async (p, side) => {
			const a = await p;
			a.push((await this.viewer.takeSaveScreenshotGetURL({
				takeBackground: false,
				width: 512,
				height: 256,
				camera: this.getScreenshotViewpoint(side)
			})).screenshot);
			return a;
		}, Promise.resolve([]));
		await currentProduct.setThumbnailsURLs(screenshots);
		const save = await this.viewer.saveModularConfiguration({
			screenshot: true,
			shortLink: true,
			unique: true
		});
		await currentProduct.setConfigurationCode(save.code);
		await currentProduct.setConfigurationReplayURL(<string>save.shortLink);
		if (!this.basket.basket.metaData) {
			this.basket.basket.metaData = {};
		}
		const md = <any>this.basket.basket.metaData;
		md.replay = await this.generateConfigurationMetadata();
		if (!this.basket.basket.deliveryContact) {
			this.component_throwLibraryError('', {});
		}
		md.storeLocation = {
			salon: (<any>this.basket.basket.deliveryContact.metadata).event,
			location: (<any>this.basket.basket.deliveryContact.metadata).position
		};
		const snapshot = await this.basket.basket.proceedCheckout({
			builderType: null,
			isQuoteRequest: true,
			isOrderRequest: false,
			useTwoTimesCreation: true,
			twoTimesCallback: async (basketCode: string) => {
				await currentProduct.setConfiguratorReplayURL(STD.net.URL_format(`${this.getAppURL().
				replace(this.code, 'replay')}/${this.code}`, {
					basketCode,
					mode: 'client'
				}));
			}
		});
		await loader.update('processCheckout', null);
		if (!app.currentProduct || !app.currentProduct.configurationCode) {
			this.component_throwLibraryError('', {});
		}
		if (!this.basket.basket.deliveryContact || !this.basket.basket.deliveryContact.metadata) {
			this.component_throwLibraryError('', {});
		}
		await loader.update('getScreenshots', null);
		if (!currentProduct.configuratorReplayURL) {
			this.component_throwLibraryError('', {});
		}
		if (app.parameters.mode === 'client' || (app.parameters.forceEmail && app.clientAppParameters.app.isTestMode)) {
			await this.sendEmail(snapshot.code, currentProduct.configuratorReplayURL);
		}
		await loader.update('finalisation', null);
		/*const replayLinkModal = <ProductReplayLinkModalNS.Component>app.component_find('@.replayLinkModal.modal.replayLink');
		if (replayLinkModal) {
			await replayLinkModal.use(currentProduct.configuratorReplayURL);
		}*/
		await loader.component_disable();
	};

	async sendEmail(basketCode: string, replayLink: string) {
		// This should probably be moved to be used globally when needed ?
		const getScreenshotFromViewpoint = async (viewpoint: TViewpoint) => {
			const viewpointCamera = this.getScreenshotViewpoint(viewpoint);
			const response = await this.viewer.getScreenshot({
				transparentBackground: true,
				width: 512,
				height: 256,
				camera: viewpointCamera,
			});
			return response.screenshot;
		};
		const loader = (<LoaderNS.Component>this.component_find('@loader'));
		if (!loader.component_isDisabled) {
			await loader.update('sendMail', null);
		}
		const urlToB64 = async (url: string): Promise<string | null> => {
			const response = await fetch(url);
			if (!response.ok) {
				const errorText = await response.text();
				throw STD.errors.library(errorText, {response});
			}
			const buffer = await response.arrayBuffer();
			return STD.DOM.arrayBufferToB64(buffer, response.type);
		};

		const versionDate = (() => {
			const now = new Date();
			const minutes = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes();
			if (this.currentLanguage === 'fr') {
				return `Version du ${now.toLocaleDateString('fr')} à ${now.getHours()}h${minutes}`;
			} else {
				return `Version of the ${now.toLocaleDateString('en')} at ${now.getHours()}h${minutes}`;
			}
		})();

		const currentProduct = this.currentProduct;
		if (!currentProduct || !currentProduct.reference) {
			this.component_throwUserError('Missing current product!', {});
		}

		const currentProductConf = this.component_configuration.PMS.products.find(x => x.SKU === currentProduct.id);
		if (!currentProductConf) {
			this.component_throwUserError('Missing current product in product available', {});
		}
		// const pdfName = currentProductConf.pdfName;
		const pdfName = 'save.pdf'; // :( /** Custom PDF name by product type is not supported in the latest version for now. **/ **/
		const route = this.clientAppParameters.server.isOnProd ?
			`${location.origin}/${this.currentLanguage}/standing/stand2024/sendEmail` :
			this.isUsingLocalViewer ? `/${this.currentLanguage}/standing/stand2024/sendEmail` :
				`https://emersya.com/${this.currentLanguage}/standing/stand2024/sendEmail`
		;
		const response = await fetch(route, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				isTest: this.clientAppParameters.app.isTestMode,
				texts: {
					productFullName: (<any>currentProductConf.translations['fullName'])[this.currentLanguage],
					replayAndShare: (<any>this.component_configuration.pdfTranslations['replayAndShare'])[this.currentLanguage],
					order: (<any>this.component_configuration.pdfTranslations['order'])[this.currentLanguage],
					orderDetail: (<any>this.component_configuration.pdfTranslations['orderDetail'])[this.currentLanguage],
					configuration: (<any>this.component_configuration.pdfTranslations['order'])[this.currentLanguage],
					profil: (<any>this.component_configuration.pdfTranslations['profil'])[this.currentLanguage],
					details: (<any>this.component_configuration.pdfTranslations['details'])[this.currentLanguage],
					htpriceinfo: (<any>this.component_configuration.pdfTranslations['htpriceinfo'])[this.currentLanguage],
					totalPrice: (<any>this.component_configuration.pdfTranslations['totalPrice'])[this.currentLanguage],
					included: (<any>this.component_configuration.pdfTranslations['included'])[this.currentLanguage],
					options: (<any>this.component_configuration.pdfTranslations['options'])[this.currentLanguage],
					priceWithoutVAT: (<any>this.component_configuration.pdfTranslations['priceWithoutVAT'])[this.currentLanguage],
					complementaries: (<any>this.component_configuration.pdfTranslations['complementaries'])[this.currentLanguage],
					'finalize.teamContact': (<any>this.component_configuration.pdfTranslations['finalize.teamContact'])[this.currentLanguage],
					'finalize.other': (<any>this.component_configuration.pdfTranslations['finalize.other'])[this.currentLanguage],
					salon: (<any>this.component_configuration.pdfTranslations['salon'])[this.currentLanguage],
					location: (<any>this.component_configuration.pdfTranslations['clientLocation'])[this.currentLanguage],
					company: (<any>this.component_configuration.pdfTranslations['company'])[this.currentLanguage],
					firstname: (<any>this.component_configuration.pdfTranslations['firstname'])[this.currentLanguage],
					lastname: (<any>this.component_configuration.pdfTranslations['lastname'])[this.currentLanguage],
					mail: (<any>this.component_configuration.pdfTranslations['mail'])[this.currentLanguage],
					phone: (<any>this.component_configuration.pdfTranslations['phone'])[this.currentLanguage],
					address: (<any>this.component_configuration.pdfTranslations['address'])[this.currentLanguage],
					city: (<any>this.component_configuration.pdfTranslations['city'])[this.currentLanguage],
					postalCode: (<any>this.component_configuration.pdfTranslations['postalCode'])[this.currentLanguage],
					country: (<any>this.component_configuration.pdfTranslations['country'])[this.currentLanguage],
					versionDate: versionDate,
					textMail: (<any>this.component_configuration.pdfTranslations['textMail'])[this.currentLanguage],
					replayAndShareMail: (<any>this.component_configuration.pdfTranslations['replayAndShareMail'])[this.currentLanguage],
					clientInformation: (<any>this.component_configuration.pdfTranslations['clientInformation'])[this.currentLanguage],
					nonContractual3DViews: (<any>this.component_configuration.pdfTranslations['nonContractual3DViews'])[this.currentLanguage]
				},
				images: {
					logo: await urlToB64(app.clientAppParameters.methodsStore.getRemoteAssetFileURL('logo.png')),
					smallLogo: await urlToB64(app.clientAppParameters.methodsStore.getRemoteAssetFileURL('icon-s.png'))
				},
				screenshots: {
					face: await getScreenshotFromViewpoint('front'),
					top: await getScreenshotFromViewpoint('top'),
				},
				fonts: {
					Iconia: await urlToB64('/bundles/EmersyaWebsitePro/fonts/Iconia/IconiaFull.otf')
				},
				basketCode: basketCode,
				pdfName: pdfName,
				replayLink: replayLink
			})
		});
		if (!response.ok) {
			const errorText = await response.text();
			try {
				const emersyaError = JSON.parse(errorText);
				throw STD.errors.library(emersyaError.description, {response, emersyaError});
			} catch (err) {
				throw STD.errors.library(errorText, {response});
			}
		}
	};

	/**
	 * @TODO : Should be named onPriceUpdated and call the syncPriceComponentWithBasket method.
	 **/
	// updatePrice(ev: STD.events.Event<any, CM.Component<any, any>>) {
	// 	console.trace();
	// 	const newPrice: BasketNS.Price = ev.details.price;
	// 	const exclTax: STD.math.BigNumber = newPrice.exclTax;
	// 	const priceComponent: TextNS.Component | undefined = <any>this.component_find('@quoteManagement.priceMetadata.price');
	// 	if (!priceComponent) {
	// 		this.component_throwLibraryError('priceComponent not found!', {});
	// 	}
	// 	const price = (Math.trunc(STD.math.round(exclTax.toNumber(), 3) * 100) / 100).toFixed(2);
	// 	priceComponent.update(price.toString());
	// };
	syncPriceComponentWithBasket() {
		if (!this.basket) {
			this.component_throwLibraryError('', {});
		}
		const priceComponent: OMSPriceNS.Component | undefined = <any>this.component_find('@visualization.basket.price');
		if (!priceComponent) {
			this.component_throwLibraryError('priceComponent not found!', {});
		}
		priceComponent.update(this.basket.basket.totalPrice.exclTax);
	};

	isChangingProduct = false;

	async disableLoaderWhenAppReady(whatAsBeenUpdated: string) {
		this.awaitedToBeReady.delete(whatAsBeenUpdated);
		if (!this.awaitedToBeReady.size) {
			const loader = this.component_find('@loader') as CM.Component.TAny;
			await this.onAppReady();
			await loader.component_disable();
		}
	};

	protected async evaluateModulesConditions() {
		for (const module of
			this.modules.used.filter<EngineModule.UIEngineModule<any>>(<(value: any) => value is EngineModule.UIEngineModule<any>>(x => x.type ===
				'ui'))) {
			await module.evaluateConditions();
		}
	};

	private transactionCount = 0;

	onChangeViewer(event: STD.events.Event<any, any>) {
		const d = <CM.DOMComponent<any, any, any>>this.component_find('K.visualization.V.loader');
		if (event.subType === 'start') {
			d.component_DOM.classList.add('viewerChanging');
		} else {
			d.component_DOM.classList.remove('viewerChanging');
		}
	};

	onKeyDown(event: Event) {

		const modals = <CM.DOMComponent<any, any, any>>app.component_find('@modals');
		if (
			!this.transactionCount ||
			modals.component_DOM.contains(event.target) // Customer infos modal is shown during a transaction so user should be able to fill it nether less
		) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
	};

	async transaction(fn: () => Promise<void> | void) {
		if (this.transactionCount === 0) {
			document.documentElement.style.pointerEvents = 'none';
			if (this.viewer.isLoaded()) {
				await app.viewer.disableMouseButtons();
				await app.viewer.disableTouchInteractions();
			}
			await this.component_find('K.visualization.V.loader').component_enable();
		}
		this.transactionCount++;
		let error;
		try {
			await fn();
		} catch (_error: any) {
			error = _error;
		}
		this.transactionCount--;
		if (this.transactionCount === 0) {
			const loader = this.component_find('K.visualization.V.loader');
			if (loader.component_isInitialized && loader.component_isStarted) {
				await loader.component_disableIfIsEnabled();
			}
			document.documentElement.style.pointerEvents = '';
			if (this.viewer.isLoaded()) {
				await this.viewer.enableMouseButtons();
				await this.viewer.enableTouchInteractions();
			}
		}
		if (error) {
			throw error;
		}
	};

	// private customerInformationFormPromise: Promise<void> | null = null;

	/** TODO : Move to planner component **/
	get width() {
		const p = app.currentProduct;
		if (!p) {
			return 0;
		}
		const dpart = p.getPart(['dimensions']);
		const width = Number.parseFloat(dpart.getPart(['width'])?.reference?.value || <string><any>1);
		return width;
	};

	/** TODO : Move to planner component **/
	get depth() {
		const p = app.currentProduct;
		if (!p) {
			return 0;
		}
		const dpart = p.getPart(['dimensions']);
		const depth = Number.parseFloat(dpart.getPart(['depth'])?.reference?.value || <string><any>1);
		return depth;
	};

	/** TODO : Move to planner component **/
	get standSurface() {
		const p = app.currentProduct;
		if (!p) {
			return 0;
		}
		const dpart = p.getPart(['dimensions']);
		const width = Number.parseFloat(dpart.getPart(['width'])?.reference?.value || <string><any>1);
		const depth = Number.parseFloat(dpart.getPart(['depth'])?.reference?.value || <string><any>1);
		return width * depth;
	};

	/** TODO : Move to planner component **/
	async onPlannerUpdated(ev: STD.events.Event<any, CM.Component<any, any>>) {
		void ev;
		await Promise.resolve();
	};

	/** TODO : Move to planner component **/
	getFloorThickness() {
		return 0.01;
	};


	/** TODO : Move to planner component **/
	getWDH(refresh: boolean = false) {
		refresh = true;
		if (refresh !== true) {
			return (this.wdh);
		}
		if (!this.currentProduct) {
			this.component_throwUserError('', {});
		}
		const p = this.currentProduct;
		if (!p) {
			this.component_throwLibraryError('', {});
		}
		const shapePart = this.currentProduct.getPart(['dimensions']);
		const shapeWidthPart = shapePart.getPart(['width']);
		const shapeDepthPart = shapePart.getPart(['depth']);
		const shapeHeightPart = shapePart.getPart(['height']);
		if (
			!shapeWidthPart.reference ||
			!shapeDepthPart.reference ||
			!shapeHeightPart.reference
		) {
			return ({
				width: 0,
				depth: 0,
				height: 0
			});
		}
		return ({
			width: parseFloat(shapeWidthPart.reference.value),
			depth: parseFloat(shapeDepthPart.reference.value),
			height: parseFloat(shapeHeightPart.reference.value)
		});
	};

	async basketUpdate(event: STD.events.Event<any, CM.Component<any, any>>) {
		if (event.subType === 'productAdded') {
			return;
		}
		if (this.isReloading && ((!this.currentProduct && event.subType !== 'productUsed'))) {
			return;
		}
		await this.transaction(async () => {
			if (
				!this.basket ||
				!event.subType ||
				['option-volume-updated', 'productAdded'].includes(event.subType) ||
				(event.subType !== 'productUsed' && !app.viewer.isLoaded())
			) {
				return;
			}
			const batch: Viewer.IBatchDescriptor = [];
			if (event.subType === 'productUsed') {
				this.isChangingProduct = true;
				// if (!app.isReloading) {
				// Specific standing...
				// if (this.hasThisModuleUsed('oms-customerModal') && app.parameters.mode !== 'client') {
				// 	const modalZone = <ModalsZoneNS.Component>this.component_find('@.modals');
				// 	const customerInformationModal = <OMSCustomerModalNS.Component>this.component_find('@.modals.oms-customerModal');
				// 	// this.customerInformationFormPromise = <Promise<void>> <any> customerInformationModal.use();
				// 	await modalZone.useModal(customerInformationModal, null);
				// }
				// }
				if (!this.isReloading) {
					// if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					// 	await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-walls-editor.content')).clear();
					// }
					// if (this.hasThisModuleUsed('viewer-planner-floor-walls')) {
					// 	await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-floor-walls.content:wallsMenu')).clear();
					// }
				}
				if (this.viewer.planner /* atchoum */ && app.viewer.isLoaded()) {
					// for (const instance of Array.from(this.viewer.safe.value.productInstances)) {
					// 	await instance.delete();
					// }
				}
				if (this.basket.basket.currentProduct) {
					if (this.isReloading) {
						// 	if (!this.previousConfigurationMetadata) {
						// 		this.component_throwLibraryError('Reloading app should have previousConfigurationMetadata defined!', {});
						// 	}
						// 	const PCM = this.previousConfigurationMetadata;
						// 	const vPath = app.viewer.component_getPath();
						// 	const viewerValue = PCM.userInputs.find(x => x[0] === vPath);
						// 	if (!viewerValue) {
						// 		this.component_throwLibraryError('', {});
						// 	}
						// 	const v = await this.viewer.deserialize({
						// 		...<ViewerNS.ValueSerialized>viewerValue[1],
						// 		configurationCode: (
						// 			this.basket.basket.currentProduct.configurationCode ||
						// 			this.previousConfigurationMetadata?.basket.products[0]?.configurationCode ||
						// 			''
						// 		)
						// 	});
						// 	await this.viewer.updateValue(v);
					} else {
						const v = await this.viewer.deserialize({
							baseLocalId: 2,
							productInstances: [],
							configurationCode: this.basket.basket.currentProduct.configurationCode,
							viewerCode: this.basket.basket.currentProduct.viewerCode
						});
						await this.viewer.updateValue(v);
					}
				}
				if (this.viewer.component_isDisabled) {
					await this.viewer.component_enable();
				}
				const viewerLoader = <CM.DOMComponent<any, any, any>>app.component_find('K.visualization.V.loader');
				viewerLoader.component_DOM.classList.add('viewerChanging');
				this.updateGlobalProductName();
				if (this.hasThisModuleUsed('viewer-planner-furniture-editor')) {
					await (<PlannerFurnitureEditorNS.Component>app.component_find(
						'@modules:panel.viewer-planner-furniture-editor.content')).update();
				}
				if (this.hasThisModuleUsed('viewer-planner-floor-editor')) {
					await (<PlannerFloorEditorNS.Component>app.component_find('@modules:panel.viewer-planner-floor-editor.content')).reset(
						event);
				}
				if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-walls-editor.content')).instantiateMenu(
						event);
				}
				await this.ifHasThisModuleUsed('viewer-planner-floor-walls', async (module) => {
					if (module.component) {
						await (<PlannerFloorEditorNS.Component>module.component.component_find('@:floorMenu')).reset(event);
						await (<PlannerWallsEditorNS.Component>module.component.component_find('@:wallsMenu')).instantiateMenu(event);
					}
				});
				if (this.hasThisModuleUsed('viewer-planner-layout-editor')) {
					const c = (<PlannerLayoutEditorNS.Component>app.component_find(
						'@modules:panel.viewer-planner-layout-editor.content'));
					c.component_configuration.defaultValue = 'shape-back-right';
					await c.reset();
				}
				if (this.hasThisModuleUsed('viewer-planner-dimensionsEditor')) {
					await (<PlannerDimensionsEditorNS.Component>app.component_find(
						'@modules:panel.viewer-planner-dimensionsEditor.content')).reset();
				}
				const layoutModule = app.modules.used.find(x => x.id === 'viewer-planner-layout-editor');
				if (layoutModule && layoutModule.isUsed && layoutModule.component) {
					await (<InputSelectNS.Component>layoutModule.component).updateValueFromId('shape-back-right');
				}
				// this.customerInformationFormPromise = null;
				viewerLoader.component_DOM.classList.remove('viewerChanging');
				this.isChangingProduct = false;
				if (this.hasThisModuleUsed('oms-customerForm')) {
					try {
						(<OMSCustomerFormButtonNS.Component>this.component_find('K.visualization.topRightMenu.customerInformationButton')).component_DOM.classList.remove(
							'notReady');
					} catch (error) {
					}
				}
				const configuratorMenu = <MenuConfiguratorNS.Component>this.component_find('K.modules');
				await configuratorMenu.refreshIndex();
			} else if (event.details.part) {
				const path = event.details.part.getPath().replace(event.details.product.id, '');
				let volumeMustBeUpdated = false;
				if (path.startsWith('.dimensions.')) {
					if (!this.viewer.planner) {
						this.component_throwLibraryError('', {});
					}
					volumeMustBeUpdated = true;
					// const DE = app.modules.used.find(x => x.id === 'viewer-planner-dimensionsEditor');
					// if (DE && !DE.configuration.isStaticSize) {
					// 	await this.viewer.planner.updateSize({
					// 		[event.details.part.id]: Number.parseFloat(event.details.reference.value)
					// 	});
					// }
					// if (this.hasThisModuleUsed('standing')) {
					// 	if (!this.currentProduct) {
					// 		this.component_throwUserError('Missing current product while dimensions updated', {});
					// 	}
					// 	const dpart = this.currentProduct.getPart(['dimensions']);
					// 	const width = dpart.getPart(['width'])?.reference?.value || <string><any>1;
					// 	const depth = dpart.getPart(['depth'])?.reference?.value || <string><any>1;
					// 	(<TextNS.Component>app.component_find('K.visualization.topRightMenu.dimensions')).update(width +
					// 		'm x ' +
					// 		depth +
					// 		'm');
					// }
					// if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					// 	await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-walls-editor.content')).updateSize();
					// }
					// if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					// 	await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-walls-editor.content')).updateSize();
					// }
				} else if (!event.details.part.metaData || !event.details.part.metaData.D3Component) {
					if (path.startsWith('.floor')) {
						// icicicicici
						const option = event.details.part.currentOption;
						if (!app.viewer.planner) {
							this.component_throwLibraryError('', {});
						}
						if (option.metaData.viewerOption.type === 'group') {
							app.viewer.planner.msSetting = option.metaData.viewerOption.configurationCode;
						} else if (option.metaData.viewerOption.type === 'groups') {
							app.viewer.planner.msSetting = option.metaData.viewerOption.values[0].configurationCode;
						} else {
							throw STD.errors.user('Only group(s) are allowed for floor', {});
						}
						await app.viewer.applyOption(option.metaData.viewerOption);
						volumeMustBeUpdated = true;
						// await app.viewer.planner.updateGroundUV();
					} else if (path.startsWith('.wall')) {
						if (!this.isChangingProduct) {
							const option = event.details.part.currentOption;
							if (!app.viewer.planner) {
								this.component_throwLibraryError('', {});
							}
							if (option) {
								await app.viewer.applyOption(option.metaData.viewerOption);
							}
						}
					}
				}
				// else if (path.startsWith('.layout')) {
				// 	if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
				// 		// yes we need to update 2 times... First time storage and partitions heads was not ready...
				// 		await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-walls-editor.content')).update();
				// 		await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-walls-editor.content')).update();
				// 	}
				// 	if (this.hasThisModuleUsed('viewer-planner-floor-walls')) {
				// 		await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-floor-walls.content:wallsMenu')).update();
				// 		await (<PlannerWallsEditorNS.Component>app.component_find('@modules:panel.viewer-planner-floor-walls.content:wallsMenu')).update();
				// 	}
				// 	await this.customerInformationFormPromise;
				// 	const option = event.details.part.currentOption;
				// 	if (option.id === 'shape-no-wall') {
				// 	} else {
				// 	}
				// } else if (path.startsWith('.personalization')) {
				// 	const option = event.details.part.currentOption;
				// 	if (!app.viewer.planner) {
				// 		this.component_throwLibraryError('', {});
				// 	}
				// 	await app.viewer.applyOption(option.metaData.viewerOption);
				// } else if (path.startsWith('.carpetImprint')) {
				// 	const option = event.details.part.currentOption;
				// 	if (!app.viewer.planner) {
				// 		this.component_throwLibraryError('', {});
				// 	}
				// 	await app.viewer.applyOption(option.metaData.viewerOption);
				// } else if (path.startsWith('.counter')) {
				// 	if (!this.isChangingProduct) {
				// 		const option = event.details.part.currentOption;
				// 		if (!app.viewer.planner) {
				// 			this.component_throwLibraryError('', {});
				// 		}
				// 		if (option) {
				// 			await app.viewer.applyOption(option.metaData.viewerOption);
				// 		}
				// 	}
				// } else {
				// 	if (event.subType ===
				// 		'partOptionUsed' &&
				// 		event.details.part.reference &&
				// 		(event.details.part.reference.value === 'furniture-plant-siae')
				// 	) {
				// 		if (!this.viewer.planner) {
				// 			throw new Error;
				// 		}
				// 		const product = this.viewer.categories.map(x => x.products).
				// 		flat().
				// 		find(x => x.SKU === 'furniture-plant-siae');
				// 		if (!product) {
				// 			throw new Error;
				// 		}
				// 		const mod = this.modules.used.find(x => x.id === 'viewer-planner-furniture-editor');
				// 		if (mod && mod.component) {
				// 			const c = mod.component.component_find('@:menu').
				// 			component_outerChilds.
				// 			map(x => x.component_find('@:content')).
				// 			map(x => x.component_outerChilds.find(x => x.id === 'furniture-plant-siae')).
				// 			filter(x => x)[0];
				// 			if (c) {
				// 				(<CM.DOMComponent<any, any, any>>c).component_DOM.classList.add('limitReached');
				// 				(<CM.DOMComponent<any, any, any>>c).component_DOM.title =
				// 					app.currentLanguage === 'fr' ? 'Limité à un seul exemplaire' : 'Limited to one copy';
				// 			}
				// 		}
				// 	}
				// 	if (event.subType ===
				// 		'partRemoved' &&
				// 		event.details.part.reference &&
				// 		event.details.part.reference.value ===
				// 		'furniture-plant-siae') {
				// 		if (!this.viewer.planner) {
				// 			throw new Error;
				// 		}
				// 		const product = this.viewer.categories.map(x => x.products).
				// 		flat().
				// 		find(x => x.SKU === 'furniture-plant-siae');
				// 		if (!product) {
				// 			throw new Error;
				// 		}
				// 		if (!product.instances.size) {
				// 			const mod = this.modules.used.find(x => x.id === 'viewer-planner-furniture-editor');
				// 			if (mod && mod.component) {
				// 				const c = mod.component.component_find('@:menu').
				// 				component_outerChilds.
				// 				map(x => x.component_find('@:content')).
				// 				map(x => x.component_outerChilds.find(x => x.id === 'furniture-plant-siae')).
				// 				filter(x => x)[0];
				// 				if (c) {
				// 					(<CM.DOMComponent<any, any, any>>c).component_DOM.classList.remove('limitReached');
				// 					(<CM.DOMComponent<any, any, any>>c).component_DOM.title = '';
				// 				}
				// 			}
				// 		}
				// 	}
				// }
				if (volumeMustBeUpdated) {
					const currentProduct = app.currentProduct;
					if (!currentProduct) {
						this.component_throwUserError('Missing current product', {});
					}
					const floorPart = currentProduct.parts.find(x => x.id === 'floor');
					const dimensionsPart = currentProduct.parts.find(x => x.id === 'dimensions');
					if (floorPart && dimensionsPart) {
						const FPCurrentOption = floorPart.currentOption;
						if (FPCurrentOption) {
							const VCMBT = FPCurrentOption.pricingInfo?.volumeComputationMethod.boundTo || 'unit';
							if (VCMBT === 'm') {
								this.component_throwUserError('Floor option use "m" as volume computation method but it is unsupported!', {});
							} else if (VCMBT === 'unit') {
								await FPCurrentOption.updateVolume(1);
							} else if (VCMBT === 'm2') {
								const WDH = this.getWDH();
								await FPCurrentOption.updateVolume(WDH.width * WDH.depth);
							} else if (VCMBT === 'm3') {
								const WDH = this.getWDH();
								await FPCurrentOption.updateVolume(WDH.width * WDH.depth * WDH.height);
							}
						}
					}
				}
			} else {
			}
			if (batch.length) {
				await this.viewer.batch(batch);
			}
			await this.evaluateModulesConditions();
		});
		if (this.basket) {
			await this.basket.basket.computePrice();
			this.syncPriceComponentWithBasket();
		}
	};

	getModelById(modelId: string) {
		void modelId;
	};

	async applyImageTransform(
		zone: {
			width: number;
			height: number;
			mesh: string;
			nodeLocalId: number;
		},
		image: {
			resolutionX: number;
			resolutionY: number;
			left: number;
			up: number;
			scale: number;
			rotation: number;
		}
	) {
		let size;
		let baseOffsetX;
		let baseOffsetY;
		const kImg = image.resolutionX / image.resolutionY;
		const kWall = zone.width / zone.height;
		let kMin;
		if (kImg > kWall) {
			size = zone.width * image.scale;
			kMin = image.resolutionY;
			baseOffsetX = size / 2;
			baseOffsetY = (size * ((image.resolutionY / image.resolutionX))) / 2;
		} else {
			size = zone.height * image.scale;
			kMin = image.resolutionX;
			baseOffsetX = (size * ((image.resolutionX / image.resolutionY))) / 2;
			baseOffsetY = size / 2;
		}
		void baseOffsetX; //used to align left or right (center used currently)
		void baseOffsetY; //used to align top  or bottom(center used currently)
		const transform = {
			translation: [
				(0.5 - (zone.width / 2)) - image.left,
				(0.5 - (zone.height / 2)) - image.up
			] as [number, number],
			scaling: [
				(image.resolutionY / kMin) / size,
				(image.resolutionX / kMin) / size
			] as [number, number],
			rotationCenter: [
				(zone.width / 2) + image.left,
				(zone.height / 2) + image.up
			] as [number, number],
			rotation: image.rotation,
			scaleX: zone.width,
			scaleY: zone.height
		};
		if (!this.viewer.viewerWindow || (this.viewer.loadingDetails.progress !== 1)) {
			await this.viewer.awaitLoading();
		}
		await this.viewer.transformMeshInstancesTextures([
			{
				name: zone.mesh,
				nodeLocalId: zone.nodeLocalId,
				transform
			}
		]);
	};

	async onBasketPartReferenceUpdated(ev: STD.events.Event<any, CM.Component<any, any>>) {
		const part = <BasketNS.Part>ev.details.part;
		if (
			(
				(part.id !== 'width') &&
				(part.id !== 'depth') &&
				(part.id !== 'height')
			) || (part.parent.id !== 'shape') ||
			!(part.parent instanceof BasketNS.Part) ||
			(part.parent.parent !== ev.details.product)
		) {
			return;
		}
		const details = {
			old: this.wdh,
			new: this.getWDH(true)
		};
		//nothing updated
		if (
			(details.old.width === details.new.width) &&
			(details.old.depth === details.new.depth) &&
			(details.old.height === details.new.height)
		) {
			return;
		}
		this.wdh = details.new;
		await this.dispatchEvent('standDimensions-update', {details});
	};

	async smallRMOpenCloseMenu(isOpen: boolean) {//eslint-disable-line require-await
		if (isOpen) {
			this.component_DOM.classList.remove('smallRMMenuClosed');
		} else {
			this.component_DOM.classList.add('smallRMMenuClosed');
		}
	};

	updateGlobalPanelStateCSSStatus(ev: STD.events.Event<any, CM.Component<any, any>>) {
		if ((<any>ev.target).currentSection) {
			this.component_DOM.classList.add('panelOpened');
		} else {
			this.component_DOM.classList.remove('panelOpened');
		}
	};

	private readonly awaitedToBeReady = new Set([
		'viewer',
		'planner',
		'app'
	]);

	/** TODO : Move to planner component **/
	private wdh = {
		width: 0,
		depth: 0,
		height: 0
	};

	// private async onMeshHighlight(ev : STD.events.Event<any, CM.Component<any, any>>) {
	// 	const hoveredNodeLocalId : number[] = ev.details.nodeLocalIds;
	// 	let instance : TProductInstance | null = null;
	// 	for (const _instance of this.planner.planner.instances) {
	// 		if (_instance.localsIds.find(x => hoveredNodeLocalId.includes(x))) {
	// 			instance = _instance;
	// 			break;
	// 		}
	// 	}
	// 	await this.viewer.setHighlight({
	// 		nodeLocalIds : instance ? instance.localsIds : []
	// 	});
	// };


	/** TODO : Move to planner component **/
	private computeStandBoundingBox = () => {
		const {width: x, depth: z, height: y} = this.getWDH();
		const bb = {
			width: 0,
			height: 0,
			depth: 0,
			maxX: 0.5 * x,
			maxY: y,
			maxZ: 0.5 * z,
			minX: -0.5 * x,
			minY: -0.005,
			minZ: -0.5 * z
		};
		bb.width = bb.maxX - bb.minX;
		bb.height = bb.maxY - bb.minY;
		bb.depth = bb.maxZ - bb.minZ;
		return (bb);
	};

	private async onAppReady() {
		const ev = this.component_getEventWhichRepresentNoEvent();
		for (const c of this.component_outerChilds) {
			if (c.component_configuration['@disableAutoStart']) {
				await c.component_start(ev);
			}
		}
		// (<any>document.querySelector('iframe')).contentWindow.emViewer.STD.exec('', 'rdr.toggleBBoxes');
	};

	computePriceForComponentsWhichHasSomeFreeElement(
		basketProduct: BasketNS.Product,
		productComponent: Viewer.ProductInstance
	): number {
		if (basketProduct.id === 'siae') {
			const mapping: Record<
				string,
				{
					readonly freeItemsCount: number;
					readonly price: number;
				}
			> = {
				'siae:furniture-seat-c': {
					freeItemsCount: 4,
					price: 72
				},
				'siae:furniture-table': {
					freeItemsCount: 1,
					price: 80
				},
				'siae:furniture-briefcase-siae': {
					freeItemsCount: 1,
					price: 65
				},
				'siae:furniture-trash': {
					freeItemsCount: 1,
					price: 7
				}
			};
			// const freeItemCount = mapping[productComponent.product.SKU];
			const pricingForThisProductComponent = mapping[`${productComponent.name}:${productComponent.product.SKU}`];
			if (!pricingForThisProductComponent) {
				this.component_throwLibraryError('', {});
			}
			if (productComponent.order <= pricingForThisProductComponent.freeItemsCount) {
				return 0;
			}
			return pricingForThisProductComponent.price;
		}
		this.component_throwLibraryError('not implemented!', {});
	};

	/**
	 * @TODO : Was for standing, should be better implemeted now!
	 **/
	async fillComponentOptions(
		fromMenuComponent: CM.DOMComponent<any, any, any>,
		basketPart: BasketNS.Part,
		inputSelect: InputSelectNS.Component,
		optionsDescriptors: PMS.Option[],
		configuration: PMS.Configuration,
		optionComponentType: string,
		event: STD.events.Event<CM.Component<any, any>, CM.Component<any, any>>,
		allFree?: boolean | null
	) {
		void configuration;
		/** Configuration is not used for now, but it will be necessary in the future for preset / group handling. **/
		const optionsInfos = optionsDescriptors.map(optionDescriptor => {
			const translations = optionDescriptor.translations['@name'];
			if (!translations) {
				this.component_throwUserError(`Missing traduction @name for floor option<${optionDescriptor.SKU}>!`, {});
			}
			const currentTrad = translations[this.currentLanguage];
			if (!currentTrad) {
				this.component_throwUserError(`Missing traduction @name for floor option<${optionDescriptor.SKU}> for language<${this.currentLanguage}>!`, {});
			}
			//
			//
			const option = basketPart.options.find(x => x.id === optionDescriptor.SKU) || basketPart.createOption({
				id: optionDescriptor.SKU,
				title: currentTrad,
				reference: {value: optionDescriptor.SKU},
				components: [],
				thumbnail: optionDescriptor.thumbnail,
				metaData: <any>{
					// viewerOption: optionDescriptor.SKU,
					viewerOption: {
						type: configuration.type,
						groupCode: configuration.SKU,
						configurationCode: optionDescriptor.SKU
					},
					translations: translations,
					/** Old standing code, now SKU is the same as the option or preset name. **/
					// SKU: optionDescriptor.additionalData?.SKU,
					/** RGB is an old Standing code! **/
					// RGB: optionDescriptor.additionalData?.RGB,
					icon: optionDescriptor.thumbnail
				},
				pricingInfo: optionDescriptor.pricingInfo ? {
					pricing: BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>optionDescriptor.pricingInfo.pricing),
					volumeComputationMethod: optionDescriptor.pricingInfo.volumeComputationMethod
				} : null,
				technicalFees: null
			});
			//
			//
			if (!option) {
				this.component_throwLibraryError('', {});
			}
			return {
				descriptor: optionDescriptor,
				option
			};
		});
		const x2f = async (optionsInfo: typeof optionsInfos) => {
			for (const optionInfo of optionsInfo) {
				// const price = optionInfo.option.pricingInfo?.pricing.resolve() || null;
				const tr = STD.translationsManager.languages.reduce<Record<string, string>>((a, c) => {
					a[c] = optionInfo.descriptor.SKU;
					return a;
				}, {});
				const tile = <InputButtonNS.Component>await inputSelect.component_instantiateOuterChild<ProductOptionTileNS.Configuration>(
					{
						type: optionComponentType,
						id: optionInfo.option.id,
						tree: this.component_tree,
						slot: null,
						isSlot: false,
						configuration: {
							option: optionInfo.option,
							// component: optionInfo.descriptor.component,
							'@innerConfigurations': {
								'image': <ImageNS.Configuration>{
									'@translations': {
										'name': tr
									},
									'URL': optionInfo.descriptor.thumbnail.type === 'image' ? optionInfo.descriptor.thumbnail.value : null,
									'@CSS': {
										backgroundColor: optionInfo.descriptor.thumbnail.type === 'color' ? optionInfo.descriptor.thumbnail.value : '',
										height: '100%'
									}
								},
							},
							'@translations': {
								text: optionInfo.descriptor.translations['@name']
								// 	/** Old Standing code. **/
								// 	STD.translationsManager.languages.reduce<STD.translationsManager.TranslationMapping>((a, c) => {
								// 	a[c] =
								// 	// 	`${(<STD.translationsManager.TranslationMapping>optionInfo.descriptor.translations['@name'])[c]} ${optionInfo.descriptor.additionalData ?
								// 	// 		'(' + optionInfo.descriptor.additionalData.SKU + ')' :
								// 	// 		''}`;
								// 	return a;
								// }, {})
							}
						}
					}
				);
				await tile.component_initialize();
				await tile.component_start(event);
			}
		};
		const componentWhichHasTheTranslations = (<any>fromMenuComponent.component_configuration['@translations'])?.optional ?
			fromMenuComponent :
			this;
		this.component_defineTranslation('included', (<any>this.component_configuration['@translations']).included, true);
		this.component_defineTranslation(
			'optional',
			(<any>componentWhichHasTheTranslations.component_configuration['@translations']).optional,
			true
		);
		const includedOptions = (await Promise.all(optionsInfos.map(async x => [
			x,
			!x.option.pricingInfo || (await x.option.pricingInfo.pricing.resolve()).isNull()
		] as const))).filter(x => x[1]).map(x => x[0]);
		const inOptionOptions = optionsInfos.filter(x => !includedOptions.includes(x));
		if (!allFree) {
			inputSelect.component_DOM.appendChild(STD.DOM.factory({
				tag: 'h3',
				dataset: {
					translation: <string>this.component_generateTranslationId('included')
				},
				classList: ['fake-title'],
				style: {
					marginRight: '100%'
				}
			}));
		}
		await x2f(includedOptions);
		if (inOptionOptions.length != 0) {
			inputSelect.component_DOM.appendChild(STD.DOM.factory({
				tag: 'h3',
				dataset: {
					translation: <string>componentWhichHasTheTranslations.component_generateTranslationId('optional')
				},
				classList: ['fake-title'],
				style: {
					marginRight: '100%',
					whiteSpace: 'nowrap'
				}
			}));
			await x2f(inOptionOptions);
		}
	};

	deduceCurrencyPricingKey() {
		return this.currentCurrency.id;
	};

	deduceInOptionOrIncludedPricingKey(params: { host: BasketNS.Product | BasketNS.Part | BasketNS.Option | null }) {
		const {host} = params;
		if (!host) {
			return 'inOption';
		} else if (host instanceof BasketNS.Part) {
			const componentSKU = host.id.slice(0, host.id.lastIndexOf('-'));
			const allProducts = app.viewer.categories.map(x => x.products).flat();
			const mapping: Record<
				string,
				{
					readonly freeItemsCount: number;
				}
			> = {
				'furniture-seat-c': {
					freeItemsCount: 4
				},
				'furniture-table': {
					freeItemsCount: 1
				},
				'furniture-briefcase-siae': {
					freeItemsCount: 1
				},
				'furniture-trash': {
					freeItemsCount: 1
				}
			};
			const data = mapping[componentSKU];
			const component = allProducts.find(x => x.SKU === componentSKU);
			if (!component || !data) {
				throw new Error;
			}
			const localId = (<any>host.metaData).nodeLocalId;
			const allProductInstances = Array.from(component.instances);
			const instance = allProductInstances.find(x => x.localId === localId);
			if (instance && instance.order <= data.freeItemsCount) {
				return 'included';
			}
			if ((allProductInstances.length + 1) <= data.freeItemsCount) {
				return 'included';
			}
			return 'inOption';
		} else {
		}
		throw new Error('');
	};

	protected onMenuChange(event: STD.events.Event<CM.Component<any, any>, CM.Component<any, any>>): Promise<void> | void {
		void event;
	};


	protected onExit() {

	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component> [ 0 ],
		configuration: TConfiguration
	) {
		Object.assign(
			{
				'@preventDefaultDisplayNone': true
			},
			configuration,
			{
				replayUserInputsSorter: (path: string, boundComponent: CM.Component<any, any>, currentOrder: number) => {
					void boundComponent;
					if (path === 'K.visualization.V') {
						return 0;
					} else if (path === 'K.modules:panel.product-editor.content') {
						return 1;
					} else if (path === 'K.modules:panel.viewer-planner-layout-editor.content') {
						return 2;
					}
					return currentOrder + 20;
				}
			}
		);
		super(descriptor, configuration);
		if (!(<any>window).app) {
			(<any>window).app = this;
		}
		BasketNS.Basket.currentApplication = <any>this;
		const currentCountry = configuration.countries.list.find(x => x.id === configuration.countries.default);
		if (!currentCountry) {
			this.component_throwLibraryError('Default coutry does not matching any country!', {
				defaultCountry: configuration.countries.default,
				countriesList: configuration.countries.list
			});
		}
		this._currentCountry = currentCountry.id;
		window.addEventListener('beforeunload', this.onBeforeUnload.bind(this), true);
		window.addEventListener('pagehide', this.onExit.bind(this), true);
		this.component_DOM.classList.add('text-paragraph');
		if (!this.parameters.useLocalViewers &&
			!this.clientAppParameters.server.isOnProd &&
			this.clientAppParameters.app.isTestMode) {
			(<any>this.clientAppParameters.app).URLPrefix = this.clientAppParameters.app.URLPrefix.replace(
				this.clientAppParameters.app.code,
				this.component_configuration.productionAppCode
			);
		}
		for (const [k, v] of Object.entries(this.component_configuration.theme.foreground)) {
			document.documentElement.style.setProperty(`--mpaengine-fg-${k}`, v);
		}
		for (const [k, v] of Object.entries(this.component_configuration.theme.background)) {
			document.documentElement.style.setProperty(`--mpaengine-bg-${k}`, v);
		}
		for (const [textType, properties] of Object.entries(this.component_configuration.theme.text)) {
			for (const [k, v] of Object.entries(properties)) {
				document.documentElement.style.setProperty(`--mpaengine-text-${textType}-${k}`, v);
			}
		}
		{
			const fonts = (
				Object.entries(this.component_configuration.theme.fonts).map(x => [
					'@font-face {',
					[
						['font-family', `'${x[0]}'`],
						['src', `url('${x[1]}')`]
					].map(x => `\t${x.join(' : ')};`),
					'}'
				].flat().join('\n')).join('\n')
			);
			const customCSS = (
				Object.entries(this.component_configuration.theme.customCSS).
				map(([selector, rule]) => `${selector} { ${rule} }`).
				join('\n')
			);
			document.head.appendChild(STD.DOM.factory({
				tag: 'style',
				dataset: {
					id: 'theme'
				},
				html: `
				${fonts}
				${customCSS}
			`
			}));
		}
		document.body.addEventListener('keydown', this.onKeyDown.bind(this), {capture: true});
	};
};
export namespace NS {
	export const Component = EngineApplicationComponent;
	export type Component = EngineApplicationComponent<any, IEngineApplicationComponentConfiguration>;
	export type Configuration = IEngineApplicationComponentConfiguration;

	export interface IClientAppParameters {
		readonly server: {
			readonly protocol: string;
			readonly domain: string;
			readonly host: string;
			readonly isOnProd: boolean;
			readonly webserviceURL: string;
		};
		readonly user: {
			readonly name: string | null;
			readonly lang: string;
			readonly geolocation?: {
				readonly continent: {
					readonly code: string;
				};
				readonly country: {
					readonly code: string;
					readonly locales: string;
				};
			};
		};
		readonly app: {
			readonly code: string;
			readonly isJS: boolean;
			readonly isTestMode: boolean;
			readonly customerCode: string;
			readonly assetsPath: string;
			readonly URLPrefix: string;
			readonly savedConfigurationCode: string;
			readonly savedBasket: string | null;
			readonly savedBasketProductCode: string | null;
			readonly viewer: {
				readonly route: string;
				readonly routingCode: string;
				readonly useShowcaseDev: string;
				readonly URL: string;
			};
			readonly configuration: {
				readonly path: string;
				readonly versionId: string | null;
				readonly extension: 'xml' | 'json';
				readonly remotePath: string;
				readonly currentPath: string;
				readonly isExternalUsed: boolean;
			};
		};
		readonly  methodsStoreIdentifier: string;
		readonly  methodsStore: {
			readonly getViewerURL: (
				viewerCode: string,
				options: {
					readonly forceLocal?: boolean;
				}
			) => string;
			readonly getCDNFileURL: (fileName: string) => string;
			readonly getRemoteCDNFileURL: (fileName: string) => string;
			readonly getLatestRemoteCDNFileURL: (fileName: string) => string;
			readonly getAssetFileURL: (fileName: string) => string;
			readonly getRemoteAssetFileURL: (fileName: string) => string;
			readonly getLatestRemoteAssetFileURL: (fileName: string) => string;
		};
	};
}

CM.Manager.declare({
	id: 'mpaengine-application',
	type: EngineApplicationComponent
});

import registerModuleBasket from '../../modules/oms-basket';
import registerModulePlanner from '../../modules/viewer-planner';
import registerModulePlannerLayoutEditor from '../../modules/viewer-planner-layout-editor';
import registerModulePlannerDimensionsEditor from '../../modules/viewer-planner-dimensionsEditor';
import registerModulePlannerFloorEditor from '../../modules/viewer-planner-floor-editor';
import registerModulePlannerWallsEditor from '../../modules/viewer-planner-walls-editor';
import registerModulePlannerFurniture from '../../modules/viewer-planner-furniture-editor';
import registerModuleProductChoose from '../../modules/product-editor';
import registerModuleCustomerInformationForm from '../../modules/oms-customerForm';
import registerModuleCustomerInformationFormModal from '../../modules/oms-customerFormModal';
import registerModuleCommandConfirmationModal from '../../modules/oms-processConfirmationModal';
import registerModuleLanguageSelector from '../../modules/language-selector';
import registerModulePlannerMenu from '../../modules/viewer-menu';
import registerModuleHeaderMenu from '../../modules/header-menu';
import registerModuleHeaderFullWidth from '../../modules/header-fullWidth';
import registerModuleHeaderViewer from '../../modules/header-viewer';
import registerModulePlannerViewerFloorWalls from '../../modules/viewer-planner-floor-walls';
import registerModuleHome from '../../modules/home';
import registerModuleReplayLinkModal from '../../modules/product-replayLinkModal';
import EngineModule from '../../EngineModule';
import {NS as OMSBasketNS} from '../oms/Basket/OMSBasket';
import {NS as OMSPriceNS} from '../oms/Price/OMSPrice';
import {NS as ProductOptionTileNS} from '../product/ProductOptionTile/ProductOptionTile';
import Viewer, {NS as ViewerNS} from '../viewer/Viewer/Viewer';
import {NS as ViewerGraphicsNS} from '../viewer/Graphics/ViewerGraphics';
import {NS as PlannerLayoutEditorNS} from '../viewer/planner/layout/Editor/PlannerLayoutEditor';
import {NS as PlannerDimensionsEditorNS} from '../viewer/planner/DimensionsEditor/PlannerDimensionsEditor';
import {NS as PlannerWallsEditorNS} from '../viewer/planner/walls/Editor/PlannerWallsEditor';
import {NS as PlannerFloorEditorNS} from '../viewer/planner/floor/Editor/PlannerFloorEditor';
import {NS as PlannerFurnitureEditorNS} from '../viewer/planner/furniture/Editor/PlannerFurnitureEditor';
import {NS as OMSCustomerFormButtonNS} from '../oms/CustomerFormButton/OMSCustomerFormButton';
import {NS as HistoryManagerNS} from '../HistoryManager';
import {NS as MenuConfiguratorNS} from '../ConfiguratorMenu/MPAEngineConfiguratorMenu';


